import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logger from "../../utilitis/logger";
import { useOrderTracking, OrderTrackingPopover } from "../OrderTrackingPopover";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const PAGE_SIZE = 10;

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const NursingHomeOrderHistory = () => {
  const navigate = useNavigate();
  const tracking = useOrderTracking();

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Filter inputs (draft) vs. applied filters (used for the actual query)
  const [referenceInput, setReferenceInput] = useState("");
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    reference: "",
    dateFrom: "",
    dateTo: "",
  });

  const fetchOrders = useCallback(
    async (pageToFetch, filters, append) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await axios.get("/nursinghome/orders-history", {
          withCredentials: true,
          params: {
            page: pageToFetch,
            limit: PAGE_SIZE,
            reference: filters.reference || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
          },
        });

        const { data, hasMore: more, total: totalCount } = response.data;

        setOrders((prev) => (append ? [...prev, ...data] : data));
        setHasMore(more);
        setTotal(totalCount);
        setPage(pageToFetch);
      } catch (err) {
        logger.error("Error fetching order history:", err);
        setError("שגיאה בטעינת היסטוריית ההזמנות. אנא נסה שוב מאוחר יותר.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchOrders(1, appliedFilters, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const handleSearch = () => {
    setAppliedFilters({
      reference: referenceInput.trim(),
      dateFrom: fromDateInput,
      dateTo: toDateInput,
    });
  };

  const handleClearFilters = () => {
    setReferenceInput("");
    setFromDateInput("");
    setToDateInput("");
    setAppliedFilters({ reference: "", dateFrom: "", dateTo: "" });
  };

  const handleLoadMore = () => {
    fetchOrders(page + 1, appliedFilters, true);
  };

  const handleRemoveFilter = (key) => {
    setAppliedFilters((prev) => ({ ...prev, [key]: "" }));
    if (key === "reference") setReferenceInput("");
    if (key === "dateFrom") setFromDateInput("");
    if (key === "dateTo") setToDateInput("");
  };

  const hasActiveFilters =
    appliedFilters.reference || appliedFilters.dateFrom || appliedFilters.dateTo;

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <HistoryIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    היסטוריית הזמנות
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    כל ההזמנות שביצעת בפורטל בית אבות
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => navigate("/nursinghome")}
                sx={{ textTransform: "none" }}
              >
                חזרה לטופס הזמנה
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="מספר הזמנה"
                    placeholder="מספר הזמנה"
                    value={referenceInput}
                    onChange={(e) => setReferenceInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DatePicker
                    label="מ- תאריך"
                    format="DD/MM/YYYY"
                    value={fromDateInput ? dayjs(fromDateInput) : null}
                    onChange={(val) => setFromDateInput(val && val.isValid() ? val.format("YYYY-MM-DD") : "")}
                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <DatePicker
                    label="עד- תאריך"
                    format="DD/MM/YYYY"
                    value={toDateInput ? dayjs(toDateInput) : null}
                    onChange={(val) => setToDateInput(val && val.isValid() ? val.format("YYYY-MM-DD") : "")}
                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        borderRadius: 2,
                        py: 1,
                        boxShadow: "0 2px 8px rgba(25,118,210,0.35)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#1565c0",
                          boxShadow: "0 4px 12px rgba(25,118,210,0.45)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      חיפוש
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        variant="text"
                        onClick={handleClearFilters}
                        startIcon={<ClearIcon />}
                        sx={{ textTransform: "none", minWidth: "auto", borderRadius: 2 }}
                      >
                        נקה
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </LocalizationProvider>

            {hasActiveFilters && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
                {appliedFilters.reference && (
                  <Chip
                    size="small"
                    label={`מספר הזמנה: ${appliedFilters.reference}`}
                    onDelete={() => handleRemoveFilter("reference")}
                    sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                  />
                )}
                {appliedFilters.dateFrom && (
                  <Chip
                    size="small"
                    label={`מ- תאריך: ${dayjs(appliedFilters.dateFrom).format("DD/MM/YYYY")}`}
                    onDelete={() => handleRemoveFilter("dateFrom")}
                    sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                  />
                )}
                {appliedFilters.dateTo && (
                  <Chip
                    size="small"
                    label={`עד- תאריך: ${dayjs(appliedFilters.dateTo).format("DD/MM/YYYY")}`}
                    onDelete={() => handleRemoveFilter("dateTo")}
                    sx={{ backgroundColor: "#e3f2fd", color: "#1976d2" }}
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!loading && !error && orders.length === 0 && (
          <Card>
            <CardContent sx={{ textAlign: "center", py: 6 }}>
              <HistoryIcon sx={{ fontSize: 60, color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {hasActiveFilters
                  ? "לא נמצאו הזמנות התואמות לסינון"
                  : "עדיין לא בוצעו הזמנות"}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Order list */}
        {!loading && orders.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              מציג {orders.length} מתוך {total} הזמנות
            </Typography>

            {orders.map((order) => {
              const orderNames = (order.orders || [])
                .map((o) => o.orderName)
                .filter(Boolean);
              const hasFailures = (order.orders || []).some((o) => !o.success);
              const allItems = (order.orders || []).flatMap((o) => o.items || []);

              return (
                <Accordion key={order._id} sx={{ mb: 1.5, "&:before": { display: "none" }, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                        gap: 1,
                        pr: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                        {hasFailures ? (
                          <ErrorOutlineIcon color="warning" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        <Typography sx={{ fontWeight: 600 }}>
                          {formatDate(order.createdAt)}
                        </Typography>
                        {order.reference && (
                          <Chip
                            size="small"
                            label={`הזמנת רכש: ${order.reference}`}
                            variant="outlined"
                          />
                        )}
                        {orderNames.map((orderName) => (
                          <Chip
                            key={orderName}
                            size="small"
                            label={`מס' הזמנה: ${orderName}`}
                            onMouseEnter={(e) => tracking.handleHover(e, orderName)}
                            onMouseLeave={tracking.handleLeave}
                            sx={{
                              backgroundColor: "#e3f2fd",
                              color: "#1976d2",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#bbdefb" },
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {allItems.length} פריטים
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {order.dueDate && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>צפי ליום הפצה: </strong>
                        {formatDate(order.dueDate)}
                      </Typography>
                    )}
                    {order.notes && (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>הערות: </strong>
                        {order.notes}
                      </Typography>
                    )}
                    {hasFailures && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        חלק מפריטי ההזמנה לא נקלטו בהצלחה. לפרטים נא לפנות לשירות הלקוחות.
                      </Alert>
                    )}
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell sx={{ fontWeight: 600 }}>מק"ט</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>תיאור מוצר</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>כמות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allItems.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.PARTNAME}</TableCell>
                              <TableCell>{item.productName || ""}</TableCell>
                              <TableCell>{item.TQUANT}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Load more */}
            {hasMore && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={18} /> : null}
                  sx={{ textTransform: "none", px: 4 }}
                >
                  {loadingMore ? "טוען..." : "הזמנות נוספות"}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      <OrderTrackingPopover {...tracking} />
    </Container>
  );
};

export default NursingHomeOrderHistory;
